import { COMPANY, BRANCHES } from "./company";
import { polishSnippet, clampTitle } from "./ctr-snippets";

export const SITE_URL = "https://phuket.thainotary.co.th";
export const DEFAULT_OG_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/AA94fWlar0NrY1CKRIbY05Gk5II3/social-images/social-1779458272148-3.รูปภาพรวมทีมงานทนาย_Thai_Notary_Law_Image_6rzkaj6rzkaj6rzk.webp";

/**
 * Build a consistent set of head meta entries for a route: og + twitter +
 * absolute canonical/og:url + og:image. Pair with per-route JSON-LD scripts.
 */
export function pageHead(opts: {
  title: string;
  description: string;
  path: string; // starts with "/"
  image?: string;
  type?: "website" | "article" | "product";
  imageAlt?: string;
}) {
  const url = `${SITE_URL}${opts.path}`;
  const image = opts.image ?? DEFAULT_OG_IMAGE;
  const type = opts.type ?? "website";
  const alt = opts.imageAlt ?? opts.title;
  const isEn = opts.path.startsWith("/en");
  const description = polishSnippet(opts.description, {
    locale: isEn ? "en_US" : "th_TH",
    seed: opts.path,
  });
  return {
    meta: [
      { title: clampTitle(opts.title) },
      { name: "description", content: description },
      { property: "og:title", content: opts.title },
      { property: "og:description", content: description },
      { property: "og:type", content: type },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: alt },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: opts.title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}

// Phuket branch coordinates (real lat/lng for GEO ranking)
const BRANCH_GEO: Record<string, { lat: number; lng: number }> = {
  "phuket-town": { lat: 7.8833, lng: 98.3925 },
  patong: { lat: 7.8966, lng: 98.2966 },
  cherngtalay: { lat: 8.0019, lng: 98.2986 },
  chalong: { lat: 7.8424, lng: 98.3386 },
};

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${SITE_URL}/#organization`,
    name: COMPANY.brandName,
    legalName: COMPANY.legalName,
    url: SITE_URL,
    telephone: COMPANY.phoneIntl,
    email: COMPANY.email,
    image: DEFAULT_OG_IMAGE,
    logo: `${SITE_URL}/favicon.webp`,
    priceRange: "฿฿",
    taxID: COMPANY.registrationNumber,
    sameAs: COMPANY.websites,
    address: BRANCHES.filter((b) => b.isPrimary).map((b) => ({
      "@type": "PostalAddress",
      streetAddress: b.address,
      addressLocality: b.locality,
      postalCode: b.postalCode,
      addressCountry: "TH",
    }))[0],
    geo: {
      "@type": "GeoCoordinates",
      latitude: BRANCH_GEO["phuket-town"].lat,
      longitude: BRANCH_GEO["phuket-town"].lng,
    },
    hasMap: "https://maps.google.com/?q=Phuket+Town+Old+Town",
    location: BRANCHES.map((b) => ({
      "@type": "Place",
      name: b.name,
      address: {
        "@type": "PostalAddress",
        streetAddress: b.address,
        addressLocality: b.locality,
        postalCode: b.postalCode,
        addressCountry: "TH",
      },
      geo: BRANCH_GEO[b.id]
        ? {
            "@type": "GeoCoordinates",
            latitude: BRANCH_GEO[b.id].lat,
            longitude: BRANCH_GEO[b.id].lng,
          }
        : undefined,
    })),
    areaServed: [
      { "@type": "City", name: "Phuket" },
      { "@type": "AdministrativeArea", name: "Phuket Province" },
      { "@type": "AdministrativeArea", name: "Southern Thailand" },
    ],
    openingHours: "Mo-Su 08:00-20:00",
    knowsLanguage: ["th", "en", "ru", "zh", "de", "fr"],
    slogan: "Notary Public บนเกาะภูเก็ต — Expat · Yacht · Property · Visa",
  };
}

export function faqSchema(items: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((i, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: i.name,
      item: i.url.startsWith("http") ? i.url : `${SITE_URL}${i.url}`,
    })),
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: COMPANY.brandName,
    inLanguage: ["th-TH", "en"],
    publisher: { "@id": `${SITE_URL}/#organization` },
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

export function serviceListSchema(
  services: { name: string; description: string; url?: string; priceFrom?: number }[],
) {
  return services.map((s, idx) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${SITE_URL}/#service-${idx}`,
    name: s.name,
    description: s.description,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: [
      { "@type": "City", name: "Phuket" },
      { "@type": "AdministrativeArea", name: "Phuket Province" },
    ],
    serviceType: s.name,
    url: s.url ? (s.url.startsWith("http") ? s.url : `${SITE_URL}${s.url}`) : SITE_URL,
    ...(s.priceFrom
      ? {
          offers: {
            "@type": "Offer",
            price: s.priceFrom,
            priceCurrency: "THB",
            availability: "https://schema.org/InStock",
          },
        }
      : {}),
  }));
}

export function howToSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "วิธีใช้บริการ Notary Public ในภูเก็ต",
    description:
      "ขั้นตอนการใช้บริการ Notary Public, แปลเอกสาร และรับรองกงสุลในจังหวัดภูเก็ต ตั้งแต่ส่งเอกสารถึงรับงานเสร็จ",
    totalTime: "PT1H",
    estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: "1500" },
    step: [
      { "@type": "HowToStep", position: 1, name: "ส่งเอกสาร", text: "ถ่ายภาพหรือสแกนเอกสารส่งทาง LINE @THAINOTARY หรือ Email" },
      { "@type": "HowToStep", position: 2, name: "รับใบเสนอราคา", text: "ทีมงานในภูเก็ตประเมินราคาและระยะเวลาให้ภายใน 30 นาที" },
      { "@type": "HowToStep", position: 3, name: "นัดหมาย", text: "เลือกเข้าสำนักงาน Phuket Town / ป่าตอง / ลากูน่า / ฉลอง หรือนัด on-site ที่วิลล่า โรงแรม มารีน่า" },
      { "@type": "HowToStep", position: 4, name: "เซ็นต่อหน้าทนาย", text: "ทนาย Notary Public ที่ขึ้นทะเบียนสภาทนายความฯ ทำคำรับรองและประทับตรา" },
      { "@type": "HowToStep", position: 5, name: "รับเอกสาร", text: "รับเอกสารพร้อมใช้งานในไทยหรือต่างประเทศ ส่งทาง EMS/DHL ทั่วโลก" },
    ],
  };
}

export function speakableSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${SITE_URL}/#webpage`,
    url: SITE_URL,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["#ai-answer-tldr", "#ai-answer-facts", "h1"],
    },
  };
}

export function areasItemListSchema(
  areas: { slug: string; name: string; description?: string }[],
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "พื้นที่ให้บริการในจังหวัดภูเก็ต",
    numberOfItems: areas.length,
    itemListElement: areas.map((a, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: a.name,
      url: `${SITE_URL}/areas/${a.slug}`,
      ...(a.description ? { description: a.description } : {}),
    })),
  };
}
